import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { cn } from '@lib/utils/helpers'
import {
  APPLY_NOW_SHELL_LABELS,
  AUTH_CONTENT,
} from '../constants/authContent'
import { ApplyNowShell } from '../components/ApplyNowShell'
import { ApplyStepHeader } from '../components/ApplyStepHeader'
import { InviteCodeUsedModal } from '../components/InviteCodeUsedModal'
import { LoginStyleValidationModal } from '../components/LoginStyleValidationModal'
import {
  getInvitationData,
  type InviteRegistrationError,
} from '@lib/api/inviteService'
import { useAppDispatch } from '@/app/hooks'
import {
  setCurrentInviteId,
  setInviteData,
  clearInviteData,
} from '@/features/auth/authSlice'
import type { InviteData } from '@/types/auth'
import type {
  EnterInviteCodeProps,
  InviteCodeFormData,
  ValidationModalState,
} from '../types'

const CLOSED_VALIDATION_MODAL: ValidationModalState = {
  isOpen: false,
  title: '',
  message: '',
  confirmAction: 'ok',
  kind: 'generic',
}

const copy = AUTH_CONTENT.applyNow.step3

function isInviteAlreadyUsedError(error: InviteRegistrationError): boolean {
  return (
    error.confirmAction === 'signIn' &&
    error.message.toLowerCase().includes('already been used')
  )
}

/**
 * EnterInviteCode — mirrors Angular register `addinviteinfo` + validation dialogs.
 * Content from AUTH_CONTENT; UI via presentational components.
 */
export function EnterInviteCode({
  onVerified,
  clearOnMount = true,
}: EnterInviteCodeProps = {}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [validationModal, setValidationModal] =
    useState<ValidationModalState>(CLOSED_VALIDATION_MODAL)

  const inviteCodeSchema = useMemo(
    () =>
      z.object({
        inviteCode: z.string().min(1, copy.validation.codeRequired).trim(),
      }),
    [],
  )

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<InviteCodeFormData>({
    resolver: zodResolver(inviteCodeSchema),
    mode: 'onChange',
    defaultValues: {
      inviteCode: '',
    },
  })

  useEffect(() => {
    // A parent that already loaded an invite must opt out, or this wipes it.
    if (clearOnMount) dispatch(clearInviteData())
  }, [dispatch, clearOnMount])

  const openValidationModal = (error: InviteRegistrationError) => {
    setValidationModal({
      isOpen: true,
      title: error.modalTitle,
      message: error.message,
      confirmAction: error.confirmAction,
      email: error.email,
      kind: isInviteAlreadyUsedError(error) ? 'inviteUsed' : 'generic',
    })
  }

  const onSubmit = async (data: InviteCodeFormData) => {
    try {
      const response = await getInvitationData(data.inviteCode)

      if (response.success && response.data) {
        dispatch(setCurrentInviteId(data.inviteCode))
        dispatch(setInviteData(response.data as InviteData))
        if (onVerified) {
          onVerified(data.inviteCode, response.data as InviteData)
          return
        }
        navigate('/apply-now/verify-info')
        return
      }

      openValidationModal({
        message: response.message || copy.validation.codeInvalid,
        status: 400,
        modalTitle: 'Validation Message',
        confirmAction: 'ok',
      })
    } catch (err) {
      openValidationModal(err as InviteRegistrationError)
    }
  }

  const closeModal = () => setValidationModal(CLOSED_VALIDATION_MODAL)

  const goToSignIn = () => {
    const loginUrl = validationModal.email
      ? `/login?email=${encodeURIComponent(validationModal.email)}`
      : '/login'
    closeModal()
    navigate(loginUrl)
  }

  const handleValidationConfirm = () => {
    if (validationModal.confirmAction === 'signIn') {
      goToSignIn()
    }
  }

  const showInviteUsedModal =
    validationModal.isOpen && validationModal.kind === 'inviteUsed'
  const showGenericModal =
    validationModal.isOpen && validationModal.kind !== 'inviteUsed'

  return (
    <>
      <ApplyNowShell
        labels={APPLY_NOW_SHELL_LABELS}
        onContinue={() => void handleSubmit(onSubmit)()}
        continueLabel={isSubmitting ? copy.verifying : copy.nextButton}
        continueDisabled={isSubmitting || !isValid}
      >
        <ApplyStepHeader title={copy.title} description={copy.description} />

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mt-2"
          id="enter-invite-code-form"
        >
          <input
            type="text"
            {...register('inviteCode')}
            placeholder={copy.placeholder}
            disabled={isSubmitting}
            autoComplete="off"
            className={cn(
              'w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 outline-none focus:border-transparent focus:ring-2 focus:ring-teal-500',
              isSubmitting && 'cursor-not-allowed opacity-50',
            )}
          />
        </form>
      </ApplyNowShell>

      <InviteCodeUsedModal
        isOpen={showInviteUsedModal}
        onClose={closeModal}
        onSignIn={goToSignIn}
      />

      <LoginStyleValidationModal
        isOpen={showGenericModal}
        title={validationModal.title}
        message={validationModal.message}
        confirmText={
          validationModal.confirmAction === 'signIn'
            ? AUTH_CONTENT.common.signIn
            : AUTH_CONTENT.common.ok
        }
        onClose={closeModal}
        onConfirm={handleValidationConfirm}
      />
    </>
  )
}
